import { apiFetch, attendreTache, EN_TETE_TACHE_ASYNCHRONE } from './api';

export function getContacts(params = {}) {
  const query = new URLSearchParams(
    Object.entries(params).filter(([, value]) => value !== undefined && value !== '')
  ).toString();

  return apiFetch(`/contacts${query ? `?${query}` : ''}`);
}

export function getContactById(id) {
  return apiFetch(`/contacts/${id}`);
}

/**
 * Supprime une demande et tout ce qui en découle : devis, contrat, journées,
 * participants, convocations, pièces déposées, historique, fichiers. Sans
 * retour possible. Renvoie le bilan chiffré de ce qui est parti.
 */
export function deleteContact(id) {
  return apiFetch(`/contacts/${id}`, { method: 'DELETE' });
}

/** Saisie manuelle d'une demande (les demandes réelles arrivent par Trello). */
export function createContact(data) {
  return apiFetch('/contacts', { method: 'POST', body: JSON.stringify(data) });
}

/**
 * Déclenche l'import des cartes de la liste Trello des demandes de devis.
 * Sans idList, le serveur retombe sur ID_LISTDEVIS.
 * Renvoie un résumé { total, importes, doublons, sansCoordonnees, erreurs }.
 */
/**
 * Corrige une demande. Le serveur refuse (409) dès qu'un devis en découle :
 * le devis fige la demande telle qu'elle a été comprise.
 */
export function updateContact(id, data) {
  return apiFetch(`/contacts/${id}`, { method: 'PUT', body: JSON.stringify(data) });
}

/**
 * Lit les pièces d'un dossier (devis, contrat…) avant de saisir la demande :
 * le serveur en tire client, formations, dates et participants. Multipart,
 * hors apiFetch qui force le Content-Type JSON. Renvoie { data, pieces }.
 */
export async function extrairePiecesDemande(fichiers) {
  const corps = new FormData();
  for (const fichier of fichiers) corps.append('fichiers', fichier);
  const res = await fetch(`${process.env.REACT_APP_API_URL || '/api'}/contacts/extraction-pieces`, {
    method: 'POST',
    credentials: 'include',
    headers: EN_TETE_TACHE_ASYNCHRONE,
    body: corps,
  });
  const json = await res.json().catch(() => ({}));
  // Lancé en tâche de fond : on attend son résultat (même forme).
  if (res.status === 202 && json.tacheId) return attendreTache(json.tacheId);
  if (!res.ok) {
    const err = new Error(json?.message || `Lecture impossible (${res.status})`);
    err.status = res.status;
    throw err;
  }
  return json;
}

/**
 * Relance le client par email sur les pièces qu'il n'a pas encore déposées.
 * Refusé s'il n'y a pas de devis, ou si tout a déjà été reçu.
 */
export function rappelerDocuments(id, body = {}) {
  return apiFetch(`/contacts/${id}/documents/rappel`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

/** Le rappel tel qu'il partirait : objet, corps, destinataire. */
export function apercuRappelDocuments(id) {
  return apiFetch(`/contacts/${id}/documents/rappel/apercu`);
}

/**
 * Importe les cartes d'une liste Trello comme demandes de devis.
 *
 * `statut` est l'état dans lequel les demandes entrent ; la génération
 * automatique du devis ne se déclenche que pour « à traiter » — importer
 * en « devis envoyé » ou « terminé », c'est consigner une affaire dont le
 * devis existe déjà.
 */
export function importerDepuisTrello(idList, { statut, avecPiecesJointes } = {}) {
  return apiFetch('/contacts/import/trello', {
    method: 'POST',
    body: JSON.stringify({ idList, statut, avecPiecesJointes }),
  });
}

/**
 * Importe UNE carte Trello comme demande de devis (bouton d'une vignette
 * dans Demande de devis > Trello). Mêmes options et même résumé que l'import
 * d'une liste, plus `contactId` : la demande créée (ou déjà connue).
 */
export function importerCarteTrello(idCarte, { statut, avecPiecesJointes } = {}) {
  return apiFetch('/contacts/import/trello/carte', {
    method: 'POST',
    body: JSON.stringify({ idCarte, statut, avecPiecesJointes }),
  });
}

/**
 * Ré-importe UNE carte déjà entrée : relit la carte dans Trello et reporte
 * ses changements sur la demande (bouton « Re-importer » d'une vignette).
 * Le statut ne bouge pas, aucun devis n'est généré ; `champsModifies`
 * liste ce qui a été repris, `devisExistants` prévient qu'un devis lira
 * les nouvelles coordonnées.
 */
export function reimporterCarteTrello(idCarte, { avecPiecesJointes } = {}) {
  return apiFetch('/contacts/import/trello/carte/reimporter', {
    method: 'POST',
    body: JSON.stringify({ idCarte, avecPiecesJointes }),
  });
}

/**
 * Ajoute une entrée d'historique : c'est ce qui fait changer le statut d'une
 * demande (le « dernier statut » est la dernière entrée en date).
 */
/**
 * Notes internes de la demande : contexte supplémentaire pour la rédaction
 * du devis. Le serveur refuse (409) si le devis est déjà parti au client.
 */
export function enregistrerNotes(contactId, notes) {
  return apiFetch(`/contacts/${contactId}/notes`, {
    method: 'PUT',
    body: JSON.stringify({ notes }),
  });
}

export function ajouterHistorique(contactId, { statut, commentaire, traitePar = null }) {
  return apiFetch(`/contacts/${contactId}/historique`, {
    method: 'POST',
    body: JSON.stringify({ statut, commentaire, traitePar }),
  });
}
